import React, { useMemo, useRef, useState } from "react";
import { db } from "../firebase";
import {
  doc,
  setDoc,
  updateDoc,
  serverTimestamp,
  arrayUnion,
} from "firebase/firestore";
import { getAuth, createUserWithEmailAndPassword } from "firebase/auth";
import { Form, Button, Alert } from "react-bootstrap";

/** Generate a 5-digit random number (10000..99999) */
const makeNumberPart = () => Math.floor(100000 + Math.random() * 900000);

/** Extract first 3 alphabetical characters from name in UPPERCASE */
const makePrefix = (name) => {
  const lettersOnly = (name || "").replace(/[^a-zA-Z]/g, "");
  return lettersOnly.slice(0, 3).toUpperCase();
};

/** Slug for URLs/lookups */
const slugifyName = (name) =>
  (name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");

export default function AddCommunity() {
  const [communityName, setCommunityName] = useState("");
  const [communityAddress, setCommunityAddress] = useState("");
  const [managerEmail, setManagerEmail] = useState("");
  const [managerPassword, setManagerPassword] = useState("");
  const [managerMobile, setManagerMobile] = useState("");
  const [loading, setLoading] = useState(false);

  const [createdCommunityId, setCreatedCommunityId] = useState("");

  // Stable number for ID
  const numberPartRef = useRef(null);

  // Live preview = PREFIX + stable number
  const communityIdPreview = useMemo(() => {
    const nameTrimmed = communityName.trim();
    if (nameTrimmed && numberPartRef.current === null) {
      numberPartRef.current = makeNumberPart();
    }
    const prefix = makePrefix(nameTrimmed);
    const numberPart =
      numberPartRef.current !== null ? numberPartRef.current : "_____";
    return prefix ? `${prefix}${numberPart}` : `${numberPart}`;
  }, [communityName]);

  const handleCreateCommunity = async () => {
    const name = communityName.trim();
    const address = communityAddress.trim();
    const email = managerEmail.trim();
    const password = managerPassword;
    const mobile = managerMobile.trim();

    if (!name || !address || !email || !password) {
      alert("Please fill all fields");
      return;
    }

    // Ensure number exists
    if (numberPartRef.current === null) {
      numberPartRef.current = makeNumberPart();
    }

    const prefix = makePrefix(name); // UPPERCASE 3 letters
    const communityId = `${prefix}${numberPartRef.current}`;
    const nameSlug = slugifyName(name);

    try {
      setLoading(true);

      // ✅ STEP 1: Create the community doc FIRST (still in current/admin session)
      const communityRef = doc(db, "communities", communityId);
      await setDoc(communityRef, {
        id: communityId,
        name,
        nameSlug,
        address,
        managerEmail: email,
        description: "Your description here",
        members: [],          // empty for now; we'll push manager later
        association: [],      // [{ role, name, email, phone, uid? }, ...] later
        events: [],           // [{ id, title, date, ... }, ...] later
        createdAt: serverTimestamp(),
      });

      // ✅ STEP 2: Create the manager account (this will switch session to manager)
      const auth = getAuth();
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const managerUid = userCredential.user.uid;

      // ✅ STEP 3: Write manager data under users/{uid}
      await setDoc(
        doc(db, "users", managerUid),
        {
          // Store manager fields under the user doc (per your requirement)
          managerUid,
          managerEmail: email,
          managerMobile: mobile || null,
          // Optional normalized fields
          email,
          mobile: mobile || null,
          role: "manager",
          communities: arrayUnion(communityId),
        },
        { merge: true }
      );

      // ✅ STEP 4 (optional): add manager UID into community.members
      // Depending on your rules, this may succeed because the user is a member/manager.
      try {
        await updateDoc(communityRef, {
          members: arrayUnion(managerUid),
        });
      } catch (err) {
        console.warn(
          "Could not add manager UID to community.members (check rules):",
          err.code,
          err.message
        );
      }

      setCreatedCommunityId(communityId);
      alert("Community and manager account created successfully!");

      // Reset form & number
      setCommunityName("");
      setCommunityAddress("");
      setManagerEmail("");
      setManagerPassword("");
      setManagerMobile("");
      numberPartRef.current = null;
    } catch (error) {
      console.error("CreateCommunity error:", error.code, error.message);
      alert(error?.message || "Error creating community or manager account");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h3>Add Community</h3>

      <div className="mb-3">
        {communityIdPreview ? (
          <Alert variant="info" className="mb-2">
            <strong>Community ID (preview):</strong>{" "}
            <code>{communityIdPreview}</code>
          </Alert>
        ) : (
          <Alert variant="secondary" className="mb-2">
            Start typing the community name—I'll generate a number once and then
            prepend the first 3 letters in UPPERCASE (e.g., <code>VER12345</code>).
          </Alert>
        )}
        {createdCommunityId && (
          <Alert variant="success">
            <strong>Created Community ID:</strong>{" "}
            <code>{createdCommunityId}</code>
          </Alert>
        )}
      </div>

      <Form>
        <Form.Group className="mb-3">
          <Form.Label>Community Name</Form.Label>
          <Form.Control
            type="text"
            placeholder="e.g., Vertex Pristine"
            value={communityName}
            onChange={(e) => setCommunityName(e.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Community Address</Form.Label>
          <Form.Control
            type="text"
            placeholder="e.g., Bellandur, Bangalore - 560103"
            value={communityAddress}
            onChange={(e) => setCommunityAddress(e.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Manager Email</Form.Label>
          <Form.Control
            type="email"
            placeholder="manager@example.com"
            value={managerEmail}
            onChange={(e) => setManagerEmail(e.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Manager Password</Form.Label>
          <Form.Control
            type="password"
            placeholder="Enter a strong password"
            value={managerPassword}
            onChange={(e) => setManagerPassword(e.target.value)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Manager Mobile</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter a mobile number"
            value={managerMobile}
            onChange={(e) => setManagerMobile(e.target.value)}
          />
        </Form.Group>

        <Button
          className="mt-2"
          onClick={handleCreateCommunity}
          disabled={loading}
        >
          {loading ? "Creating..." : "Create Community"}
        </Button>
      </Form>
    </div>
  );
}
